/**
 * RowContextMenu
 *
 * Right-click popup menu for a comparison grid row: View Audit History Logs,
 * Drill-down to Timesheet Detail, Drill-down to Schedule Detail, and a
 * "Show in Engagement View" quick action.
 */
import { useEffect, useRef } from 'react';
import { Clock, ExternalLink, History, ListChecks } from 'lucide-react';

/**
 * @param {{
 *   x: number,
 *   y: number,
 *   onClose: () => void,
 *   onViewAuditHistory: () => void,
 *   onDrillDownTimesheet: () => void,
 *   onDrillDownSchedule: () => void,
 *   onShowInEngagementView: () => void,
 * }} props
 */
export const RowContextMenu = ({
  x,
  y,
  onClose,
  onViewAuditHistory,
  onDrillDownTimesheet,
  onDrillDownSchedule,
  onShowInEngagementView,
}) => {
  const menuRef = useRef(/** @type {HTMLDivElement|null} */ (null));

  // Positioned imperatively (not via a JSX style prop/object) since x/y are
  // arbitrary runtime values Tailwind's static class scanning can't cover.
  useEffect(() => {
    if (menuRef.current) {
      menuRef.current.style.top = `${y}px`;
      menuRef.current.style.left = `${x}px`;
    }
  }, [x, y]);

  useEffect(() => {
    /** @param {MouseEvent} e */
    const handleClickAway = (e) => {
      if (menuRef.current && !menuRef.current.contains(/** @type {Node} */ (e.target))) {
        onClose();
      }
    };
    const handleEscape = (/** @type {KeyboardEvent} */ e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClickAway);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickAway);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const items = [
    { label: 'View Audit History Logs', icon: History, onClick: onViewAuditHistory },
    { label: 'Drill-down to Timesheet Detail', icon: Clock, onClick: onDrillDownTimesheet },
    { label: 'Drill-down to Schedule Detail', icon: ListChecks, onClick: onDrillDownSchedule },
    { label: 'Show in Engagement View', icon: ExternalLink, onClick: onShowInEngagementView },
  ];

  return (
    <div
      ref={menuRef}
      className="fixed z-50 w-64 rounded-md border border-edge bg-base py-1 shadow-xl"
    >
      {items.map((item) => (
        <button
          key={item.label}
          type="button"
          onClick={() => {
            item.onClick();
            onClose();
          }}
          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-primary hover:bg-surface-hover"
        >
          <item.icon className="h-4 w-4 text-muted" />
          {item.label}
        </button>
      ))}
    </div>
  );
};
